/** Shop pricing and license-tier rules shared by the checkout components.
 *
 * Checkout charges listed Stripe Price IDs: one base price from the product,
 * plus an optional second line item from the selected tier's stripe_details.
 * Only the displayed total is summed here — see apps/shop/pricing.py for the
 * server side, which must agree with this file.
 */
import { derived, get, writable } from 'svelte/store';

const isRecurring = (price) => !!(price && price.type !== 'one_time' && price.recurring);

function matchesInterval(price, interval, intervalCount = 1) {
    if (!isRecurring(price)) return false;
    return price.recurring.interval === interval
        && (price.recurring.interval_count || 1) === (intervalCount || 1);
}

function monthlyEquivalent(price, interval, intervalCount) {
    const amount = price.unit_amount / 100;
    if (interval === 'year') return amount / 12;
    return intervalCount > 1 ? amount / intervalCount : amount;
}

/** Annualized savings against the monthly price, floored at zero.
 *
 * Applied in one place so the yearly badge and the plan cards can never
 * disagree, and so a term that costs more than monthly shows no "savings"
 * rather than a negative one.
 */
export function applyYearlySavings(intervals) {
    const list = intervals || [];
    const monthly = list.find((item) => item.interval === 'month' && (item.interval_count || 1) === 1);
    if (!monthly || !monthly.value) return list;

    const monthlyPerYear = monthly.value * 12;
    list.forEach((item) => {
        if (item === monthly || !item.interval) return;
        const perYear = item.interval === 'year'
            ? item.value * (1 / (item.interval_count || 1))
            : item.value * (12 / (item.interval_count || 1));
        item.originalPrice = monthlyPerYear;
        item.discount = Math.max(0, monthlyPerYear - perYear);
        if (item.interval === 'month' && (item.interval_count || 1) > 1) {
            item.termTotal = item.value;
        }
    });
    return list;
}

/** Match shop_coupon_configuration.plans options to Stripe prices. */
export function matchPlans(stripePrices, plansConfig) {
    if (!plansConfig || !plansConfig.enabled || !plansConfig.options) return [];
    const prices = stripePrices || [];
    const monthlyPlan = prices.find((p) => matchesInterval(p, 'month', 1));
    const monthlyAmount = monthlyPlan ? monthlyPlan.unit_amount / 100 : 0;

    return plansConfig.options.map((plan) => {
        let matched = plan.stripe_price_id
            ? prices.find((p) => p.id === plan.stripe_price_id)
            : null;
        if (!matched) {
            matched = prices.find((p) => matchesInterval(p, plan.interval, plan.interval_count));
        }
        if (!matched || matched.type === 'one_time') {
            return { ...plan, price: null, rawAmount: null, monthlyPrice: null, currency: null, discount: 0 };
        }
        const monthlyEquiv = monthlyEquivalent(matched, plan.interval, plan.interval_count);
        return {
            ...plan,
            price: matched,
            rawAmount: matched.unit_amount / 100,
            monthlyPrice: monthlyEquiv,
            currency: matched.currency,
            discount: monthlyAmount > 0 ? Math.max(0, (monthlyAmount - monthlyEquiv) * 12) : 0,
        };
    });
}

export function stripeDefaultPriceId(product) {
    const raw = product && (
        product.stripe_default_price_id
        || (product.stripe_product && product.stripe_product.default_price)
    );
    if (!raw) return null;
    return typeof raw === 'string' ? raw : (raw.id || null);
}

function intelTier(tiers) {
    const list = tiers || [];
    return list.find((t) => t && t.atp === false)
        || list.find((t) => String((t && t.name) || '').toLowerCase().includes('intel'))
        || null;
}

function defaultSubscriptionTier(product, config) {
    const tiers = (product && product.tiers) || [];
    const configuredGuid = config && config.subscription_tier_guid;
    if (configuredGuid) {
        const found = tiers.find((t) => t.guid === configuredGuid);
        if (found) return found;
    }
    if (tiers.length === 1) return tiers[0];
    return intelTier(tiers);
}

export function resolveSubscriptionTier(product, { selectedTierGuid, config } = {}) {
    const tiers = (product && product.tiers) || [];
    if (selectedTierGuid) {
        const selected = tiers.find((t) => t.guid === selectedTierGuid);
        if (selected) return selected;
    }
    return defaultSubscriptionTier(product, config);
}

export function shouldShowTierPicker(product, config) {
    const tiers = (product && product.tiers) || [];
    if (tiers.length < 2) return false;
    return !(config && config.subscription_tier_guid);
}

export function filterPlansForTier(matchedPlans, tierGuid, productTiers) {
    const plans = matchedPlans || [];
    if (!tierGuid) return plans;
    const labeled = plans.filter((p) => p.tier_guid);
    if (!labeled.length) return plans;
    const exact = plans.filter((p) => !p.tier_guid || p.tier_guid === tierGuid);
    if (exact.some((p) => p.tier_guid === tierGuid)) return exact;
    // Cards labelled for a tier this product does not have stay visible.
    const known = new Set((productTiers || []).map((t) => t && t.guid));
    return plans.filter((p) => !p.tier_guid || !known.has(p.tier_guid) || p.tier_guid === tierGuid);
}

export function shopCouponsWithoutTier(couponConfiguration) {
    if (!couponConfiguration) return {};
    const { tier, ...rest } = couponConfiguration;
    return rest;
}

function tierPriceForInterval(tier, interval, intervalCount = 1) {
    const details = (tier && tier.stripe_details) || [];
    const list = Array.isArray(details) ? details : [];
    return list.find((p) => matchesInterval(p, interval, intervalCount)) || null;
}

function tierAddOnCents(tier, interval, intervalCount = 1) {
    const match = tierPriceForInterval(tier, interval, intervalCount);
    return match && match.unit_amount != null ? match.unit_amount : 0;
}

/** What a trial renews at: the server's base renewal price plus the tier add-on.
 *
 * The server resolves the renewal price from the Stripe product's default_price
 * and knows nothing about the selected tier, but the trial converts to the same
 * two-item subscription as a normal purchase. Without adding the tier here the
 * quote reads low until Stripe's next-invoice preview arrives, and then jumps.
 * Pass `tier` as null when a plan card is selected, since its price is all-in.
 */
export function renewalTotalWithTier(renewalAmountCents, tier, interval, intervalCount = 1) {
    if (!renewalAmountCents || renewalAmountCents <= 0) return null;
    return (renewalAmountCents + tierAddOnCents(tier, interval, intervalCount)) / 100;
}

/** A plan card carries its own all-in price, so no tier add-on is charged. */
export function shouldAttachTierLineItem(selectedPlan) {
    return !(selectedPlan && selectedPlan.stripe_price_id);
}

/** Whether this product bills its tier as a separate line item.
 *
 * Mirrors `tier_is_add_on` in apps/shop/pricing.py. Only products offering a
 * choice of tier charge the upgrade separately; a single-tier product prices it
 * into the base price. `Coach +` is one shared tier row across products, so its
 * add-on prices exist even where the base already includes coaching, and adding
 * them there quotes (and bills) the upgrade twice.
 */
export function tierIsAddOn(product) {
    return (((product && product.tiers) || []).length) > 1;
}

function pickBasePrice(stripePrices, interval, intervalCount, defaultPriceId) {
    const matches = (stripePrices || []).filter((p) => matchesInterval(p, interval, intervalCount));
    if (defaultPriceId) {
        const preferred = matches.find((p) => p.id === defaultPriceId);
        if (preferred) return preferred;
    }
    return matches[0] || null;
}

export function checkoutPricePayload({ product, tier, interval, intervalCount, selectedPlan }) {
    if (selectedPlan && selectedPlan.stripe_price_id) {
        return {
            stripe_price_id: selectedPlan.stripe_price_id,
            price: { base_price: selectedPlan.price },
        };
    }
    const base = pickBasePrice(
        product && product.stripe_price,
        interval,
        intervalCount,
        stripeDefaultPriceId(product)
    );
    const tierPrice = shouldAttachTierLineItem(selectedPlan) && tierIsAddOn(product)
        ? tierPriceForInterval(tier, interval, intervalCount)
        : null;
    const payload = {
        price: {
            base_price: base,
            ...(tierPrice && tierPrice.id ? { tier: tierPrice } : {}),
        },
    };
    if (tier && tier.guid) payload.license_tier = tier.guid;
    return payload;
}

/** The interval and count to charge: the selected plan card, else the active tab.
 *
 * `fallbackInterval` covers a tab without a recurring interval, and lets a
 * trial force monthly billing regardless of the tab on screen.
 */
export function chargeIntervalFor(intervals, activeTabIndex, selectedPlan, fallbackInterval) {
    if (selectedPlan) {
        return { interval: selectedPlan.interval, intervalCount: selectedPlan.interval_count };
    }
    const tab = (intervals || [])[activeTabIndex] || {};
    return {
        interval: tab.interval || fallbackInterval || tab.key,
        intervalCount: tab.interval_count || 1,
    };
}

export function buildIntervalTabs(stripePrices, defaultPriceId, periodLabels) {
    const labels = periodLabels || { year: 'Yearly', month: 'Monthly' };
    const acc = {};
    (stripePrices || []).forEach((price) => {
        if (!isRecurring(price)) return;
        const interval = price.recurring.interval;
        const intervalCount = price.recurring.interval_count || 1;
        const key = intervalCount > 1 ? `${intervalCount}${interval}` : interval;
        const next = {
            label: labels[key] || `${intervalCount} ${interval}s`,
            value: price.unit_amount / 100,
            currency: price.currency,
            key,
            interval,
            interval_count: intervalCount,
            price_id: price.id,
            price,
        };
        if (!acc[key] || (defaultPriceId && price.id === defaultPriceId)) {
            acc[key] = next;
        }
    });
    return applyYearlySavings(
        Object.values(acc).sort((a, b) => String(a.key).localeCompare(String(b.key)))
    );
}

/** Base price plus the tier add-on, for display only. */
export function composeIntervals(baseIntervals, tier) {
    const list = (baseIntervals || []).map((item) => {
        if (!item.interval || item.key === 'one-time') return { ...item };
        const value = item.value + tierAddOnCents(tier, item.interval, item.interval_count || 1) / 100;
        return {
            ...item,
            value,
            composed: true,
            unit_amount: Math.round(value * 100),
            discount: undefined,
            originalPrice: undefined,
        };
    });
    return applyYearlySavings(list);
}

/** Tier/plan selection shared by the full-adoption and renewal checkouts.
 *
 * `selectedTierGuid` is writable because the tier picker sets it; everything
 * else derives from it so the two components cannot drift apart.
 */
export function createPlanState(product, config, baseIntervals) {
    const plansConfig = (config && config.plans) || null;
    const plansEnabled = !!(plansConfig && plansConfig.enabled);
    const licenseTiers = (product && product.tiers) || [];
    const matchedPlans = matchPlans(product && product.stripe_price, plansConfig);

    const selectedTierGuid = writable(
        resolveSubscriptionTier(product, { config })?.guid || null
    );
    const selectedPlanKey = writable(
        plansEnabled && plansConfig.default ? plansConfig.default : null
    );

    const visiblePlans = derived(selectedTierGuid, (guid) =>
        filterPlansForTier(matchedPlans, guid, licenseTiers));

    const tierObject = derived(selectedTierGuid, (guid) =>
        resolveSubscriptionTier(product, { selectedTierGuid: guid, config }));

    const selectedPlan = derived(
        [visiblePlans, selectedPlanKey],
        ([plans, key]) => (plansEnabled ? plans.find((p) => p.key === key) || null : null)
    );

    const showTierAddon = derived(
        [tierObject, selectedPlan],
        ([tier, plan]) => !!(tier && tierIsAddOn(product) && shouldAttachTierLineItem(plan))
    );

    const intervals = derived(
        [tierObject, showTierAddon],
        ([tier, addon]) => (addon ? composeIntervals(baseIntervals, tier) : baseIntervals)
    );

    // Keep the chosen card valid when switching tiers hides it.
    visiblePlans.subscribe((plans) => {
        if (!plansEnabled || !plans.length) return;
        const key = get(selectedPlanKey);
        if (plans.some((p) => p.key === key)) return;
        const fallback = plans.find((p) => p.key === plansConfig.default) || plans[0];
        selectedPlanKey.set(fallback ? fallback.key : null);
    });

    return {
        plansConfig,
        plansEnabled,
        licenseTiers,
        matchedPlans,
        showTierPicker: shouldShowTierPicker(product, config),
        selectedTierGuid,
        selectedPlanKey,
        visiblePlans,
        tierObject,
        selectedPlan,
        showTierAddon,
        intervals,
    };
}

export const TIER_REQUIRED_MESSAGE = 'Please choose Coach or Monitor.';
